<template>
  <div class="erm-request-list">
    <v-card>
      <!-- 頁面標題列（共用元件，融入卡片頂部） -->
      <ListPageHeader
        embedded
        title="經費預算申請清單"
        subtitle="管理與追蹤各項經費預算申請進度"
        icon="tabler-report-money"
        :count="pageCount"
      >
        <template #actions>
          <VBtn color="white" variant="flat" class="text-primary font-weight-bold" @click="create">
            <VIcon icon="tabler-plus" class="mr-1" />建立申請
          </VBtn>
        </template>
      </ListPageHeader>

      <v-card-item>
        <div class="d-flex align-center mb-1">
          <VIcon icon="tabler-filter" size="20" class="text-primary mr-2" />
          <span class="text-subtitle-1 font-weight-medium">篩選條件</span>
        </div>
        <v-row class="pt-2 pb-5">
          <!-- <v-col cols="12" md="3">
            <v-text-field label="經費需求申請名稱" 
            v-model="filters.name"
            clearable
            ></v-text-field>
          </v-col> -->
          <v-col cols="12" md="2">
            <v-text-field 
              label="申請單號" 
              v-model="filters.applyNo"
              clearable
              prepend-inner-icon="tabler-search"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="2">
            <v-select 
            :items="years" 
            label="所屬年度" 
            v-model="filters.year"
            item-title="rocYear"
            item-value="adYear"
            clearable
            ></v-select>
          </v-col>
          <v-col cols="12" md="5">
            <v-autocomplete 
            :items="projectNames" 
            label="項目名稱" 
            v-model="filters.projectName"
            item-value="projectId"
            item-title="projectTitle"
            clearable
            no-data-text="無項目名稱"
            prepend-inner-icon="tabler-search"
            >
            </v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <AppInput
            v-model="filters.status"
            type="select"
            :remoteItem="deSelectSelect"
            :remoteItemParams="{ tabId }"
            item-title="label"
            item-value="value"
            placeholder="請輸入狀態"
            :disabled="false"
            :loading="false"
            clearable
          />
          </v-col>
        </v-row>
      </v-card-item>

      <!-- {{items}} -->
      <InteractiveDataTable
          v-model:items-per-page="filters.size"
          v-model:page="filters.page"
          :items-length="pageCount"
          :headers="headers" 
          :loading="loading" 
          :items="items" 
          empty-text="尚無經費預算申請"
          empty-subtitle="可調整上方篩選條件，或點右上角「建立申請」新增"
          empty-icon="tabler-file-invoice"
          :items-per-page-options="[
            {value: 10, title: '10'},
            {value: 25, title: '25'},
            {value: 50, title: '50'},
            {value: 100, title: '100'}
          ]"
          :row-props="rowProps"
          :row-clickable="false"
          @row-click="markLastViewed"
        >

          <template v-slot:item.applyNo="{ item }">
            <div class="d-inline-flex align-center flex-nowrap">
              <v-btn 
              @click.stop="edit(item)"
              color="primary" 
              variant="text" 
              class="pa-0 idt-link font-weight-bold"
              >
                {{item.applyNo}}
              </v-btn>
              <v-tooltip location="top" text="複製單號">
                <template #activator="{ props: tipProps }">
                  <v-btn
                    v-bind="tipProps"
                    variant="text"
                    icon
                    size="x-small"
                    density="compact"
                    class="ms-1"
                    @click.stop="notifyCopyDocNo(item.applyNo)"
                  >
                    <VIcon icon="tabler-copy" size="18" />
                  </v-btn>
                </template>
              </v-tooltip>
            </div>
          </template>
          <template v-slot:item.projectTitle="{ item }">
            <v-tooltip location="top" :disabled="!item.projectTitle || String(item.projectTitle).length < 18">
              <template #activator="{ props: tipProps }">
                <span v-bind="tipProps" class="idt-truncate text-truncate">
                  {{ item.projectTitle || '-' }}
                </span>
              </template>
              <span>{{ item.projectTitle }}</span>
            </v-tooltip>
          </template>
          <template v-slot:item.applyDate="{ item }">
            <div class="d-inline-flex flex-column">
              <span class="d-inline-flex align-center text-medium-emphasis">
                <VIcon icon="tabler-calendar-event" size="16" class="mr-1" />
                {{ taiwanDateParts(item.applyDate).date || '-' }}
              </span>
              <span v-if="taiwanDateParts(item.applyDate).time" class="text-caption text-disabled ms-5">
                {{ taiwanDateParts(item.applyDate).time }}
              </span>
            </div>
          </template>
          <template v-slot:item.status="{ item }">
            <Status 
              :status="item.status" 
              :is-red="['Rejected', 'Revoke', 'Revoked']" 
              :is-green="['MApproved', 'FPassed', 'FApproved', 'SendAfterCorrect']"
              :is-warning="['Draft']"
              :is-info="[]"
              :is-purple="['ReturnedForRevision', 'MReturnedForRevision', 'FReturnedForRevision', 'CorrectAfterApprove']"
              :is-teal="['Reviewing']"
              :is-indigo="['Resubmitted', 'MPassed']"
              :is-primary="['Submitted']" 
              :is-secondary="['Closed']" 
              :shared-table-id="tabId" 
              btn-class="x-large" 
              font-class="18px" 
              :status-config="getStatusData" 
            />
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="d-flex align-center erm-actions">
              <v-btn 
              :loading="loading"
              v-if="item.status=='Rejected'"
              @click.stop="copyApply(item)"
              size="default" color="primary" 
              prepend-icon="tabler-copy"
              variant="tonal" 
              class="mr-2 idt-action-btn">
                複製此單
              </v-btn>
              <v-btn 
              :loading="loading"
              v-if="item.status=='Closed'&&!item.applied"
              @click.stop="goToReimbuirsement(item)"
              size="default" color="success" 
              prepend-icon="tabler-receipt"
              variant="tonal" 
              class="mr-2 idt-action-btn">
                核銷申請
              </v-btn>
              <v-btn 
              :loading="loading"
              v-if="item.status=='Closed'&&!item.applied"
              size="default" color="warning" 
              @click.stop="goToChange(item)" 
              prepend-icon="tabler-edit"
              variant="tonal" class="mr-2 idt-action-btn">
                {{item.changeNo?'再次變更':'變更申請'}}
              </v-btn>
              <v-chip v-if="item.status=='Closed'&&item.applied" color="error" variant="tonal" size="small" class="font-weight-bold">
                <VIcon icon="tabler-loader-2" size="16" start />變更流程中
              </v-chip>
              <span v-if="!(item.status=='Rejected') && !(item.status=='Closed')" class="text-disabled text-caption">—</span>
            </div>
          </template>
        </InteractiveDataTable>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onUnmounted, reactive } from "vue"
import { useRouter } from "vue-router"
import { useLastViewedRow } from '@/composables/useLastViewedRow'
import { copyPlainText } from '@/utils/deUtil'
import Status from '../tnm/StatusComponenet.vue'
definePage({
  meta: {
    action: 'manage',
    subject: 'EXPENSEREQUESTLIST',
  },
})
const loading = ref(false)
const userDataCookie = useStorage('userData', {}).value
const router = useRouter()
const filters = reactive({
  name: "",
  year: null,
  projectName: null,
  status: null,
  applyNo: "",
  page: 1,
  size: 100,
})

const FILTER_STATE_KEY = 'erm_expense_request_list_filter_state'
const restoreListFilters = () => {
  try {
    const raw = sessionStorage.getItem(FILTER_STATE_KEY)
    if (!raw) return
    const saved = JSON.parse(raw) as Record<string, unknown>
    for (const k of Object.keys(filters) as (keyof typeof filters)[]) {
      if (k in saved) (filters as Record<string, unknown>)[k] = saved[k] as never
    }
  } catch { /* ignore */ }
}
const saveListFilters = () => {
  try {
    sessionStorage.setItem(FILTER_STATE_KEY, JSON.stringify({ ...filters }))
  } catch { /* ignore */ }
}
restoreListFilters()

const projectNames = reactive([])
const statuses = reactive(["計畫通過", "初審通過", "回覆修正"])
const headers = reactive([
  { title: "申請單號", key: "applyNo", icon: "tabler-file-text" },
  { title: "項目名稱", key: "projectTitle", icon: "tabler-clipboard-text" },
  { title: "申請日期", key: "applyDate", icon: "tabler-calendar-event" },
  { title: "狀態", key: "status", icon: "tabler-flag" },
  { title: "操作", key: "actions", sortable: false, icon: "tabler-settings" },
])
const items = reactive([])
const pageCount = ref(0)
const { setLastViewed: setLastClickedId, rowProps } = useLastViewedRow('erm_expense_request_list_last_clicked')

const markLastViewed = (item: any) => {
  setLastClickedId(item?.applyNo)
}

// 拆解民國年日期字串為「日期」與「時間」兩部分
const taiwanDateParts = (raw: string) => {
  const [date = '', time = ''] = convertToTaiwanYear(raw || '', '-').split(' ')

  return { date, time }
}

const isDev = import.meta.env.MODE === 'development'
const windowOrigin = window.location.origin
// 頁面跳轉 - 核銷
const goToReimbuirsement = async(item) => {
    setLastClickedId(item.applyNo)
    await nextTick()
    router.push({ path: '/tca/erm/ExpenseReimbursement', query: { applyNo: item.applyNo } })
}
// 頁面跳轉 - 經費變更
const goToChange = async(item) => {
    setLastClickedId(item.applyNo)
    await nextTick()
    let query = item.changeNo ? { changeNo: item.changeNo, isNew: 'Y' } : { applyNo: item.applyNo }
    
    // 黃金計畫直接進入經費變更
    if (item.gold) {
      router.push({ path: '/tca/erm/ExpenseChange', query })
      return
    }

    $swal({
      icon: 'warning',
      title: '是否只修改行程不調動金額?',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "只修改行程",
      denyButtonText: "經費變更申請",
      cancelButtonText: "取消",
      focusCancel: false,
    }).then(async(result) => {
      if(result.isConfirmed){
        router.push({ path: '/tca/erm/ExpenseChangeSchedule', query })
      }else if(result.isDenied){
        router.push({ path: '/tca/erm/ExpenseChange', query })
      }
    })
}
const create = () =>{
  $swal({
    icon: 'warning',
    title: '是否建立新的經費需求申請?',
    showCancelButton: true,
    showDenyButton: true,
    confirmButtonText: "經費預算申請",
    denyButtonText: "黃金計劃申請",
    cancelButtonText: "取消",
    focusCancel: false,
  }).then(async(result) => {
    if(result.isConfirmed){
      router.push({ path: '/tca/erm/ExpenseRequest' })
    }else if(result.isDenied){
      router.push({ path: '/tca/erm/GoldPlanApply'})
    }
  })
}
// 編輯
const edit = (item) =>{
  setLastClickedId(item.applyNo)
  if(item.gold){
    router.push({ path: '/tca/erm/GoldPlanApply', query: { applyNo: item.applyNo } })
  }else{
    router.push({ path: '/tca/erm/ExpenseRequest', query: { applyNo: item.applyNo } })
  }
}

// 複製此單
const copyApply = (item) =>{
  $swal({
    icon: 'question',
    title: `複製此筆資料`,
    text: '是否複製此筆資料並儲存為草稿?',
    showCancelButton: true,
    confirmButtonText: "我要複製",
    cancelButtonText: "取消",
  }).then(async(result) => {
  /* Read more about isConfirmed, isDenied below */
    if (result.isConfirmed) {
      loading.value = true
      const copyRes = await getRejectedExpRequest(item)
      loading.value = false
      if(copyRes.statusCode==200 && !!copyRes.data){
        await fetchData()
        $swal({
          icon: 'success',
          title: `複製成功申請單`,
          text: `申請單號：${copyRes.data.expReqData.applyNo}`,
          showCancelButton: true,
          confirmButtonText: "前往申請單畫面",
          cancelButtonText: "停留在清單畫面",
        }).then(async(result) => {
          if (result.isConfirmed) {
            const copyApplyNo = items.find(item => item.applyNo == copyRes.data.expReqData.applyNo)
            if(copyApplyNo){
              edit(copyApplyNo)
            }else{
              $swal({
                icon: 'error',
                title: `前往失敗`,
                text: `請重新嘗試-直接點選申請單號${copyRes.data.expReqData.applyNo}前往編輯`,
              })
            }
          }
        })
      }else{
        $swal({
          icon: 'error',
          title: `複製失敗`,
          text: `請重新嘗試`,
        })
      }
    }
  });
}

// 所屬年度API
const { data: yearList, execute: fetchFilterYearList } = await useApi<any>(createUrl('/tca/expenserequestprojectlist/getYear', {
}))
const years = computed(() => yearList.value?.data ?? []);

// select 項目名稱
const getProjectList = async () => {
  let annual = filters.year?filters.year:null
  await $api('/tca/expenserequestprojectlist/getAllProjectList', {
    method: 'POST',
    body: {
      deptNo: null,
      isGold: null,
      annual, 
    }
  }).then((response) => {
    projectNames.splice(0, projectNames.length, ...response.data)
  }).catch((error) => {
    console.error('取得項目下拉選單失敗:', error);
  });
};

// 檢查經費需求是否有其他會結案 API  applyNo為經費需求的applyNo 
const isExistPreviosProject = async (applyNo) => {
  try {
    const resp = await $api('/tca/expensechange/isExist', {
      method: 'POST', //依據後端 restful api 的 method 設定
      body: { applyNo } // request body
    });
    // true為已經存在其他changeNo
    // false為不存在任何changeNo
    return resp.data; // 正確返回值
  } catch (error) {
    console.error("Error fetching previous project existence:", error);
    return true; // 錯誤處理，返回 false 或其他合適的值
  }
};


// 
const fetchData = async () => {
  loading.value = true
  let projectId = filters.projectName?filters.projectName:null
  let applyTitle = filters.name?filters.name:null
  let status = filters.status? filters.status:null
  let page = filters.page -1 
  let size = filters.size
  try {
    const response = await $api('/tca/expenserequestlist/getList', {
      method: 'POST',
      body: {
        applyTitle,
        deptNo: userDataCookie.depNo,
        projectId,
        status,
        applyNo: filters.applyNo ? filters.applyNo : null,
        page,
        size,
      },
    });

    // 根据响应做一些处理
    if (response.data) {
      pageCount.value = response.data.totalElements;
      items.splice(0, items.length, ...response.data.requestList)
    } else {
      // 处理错误情况
      items.splice(0, items.length)
      console.error('Fetch error:', response.statusText);
    }
  } catch (error) {
    console.error('Error during fetch:', error);
  }finally{
    loading.value = false
  }
}

const getRejectedExpRequest = async(item) => {
  try {
    const response = await $api('/tca/expenserequest/getRejectedExpRequest', {
      method: 'POST',
      params: {
        applyNo:item.applyNo
      },
    });
    // 根据响应做一些处理
    return response
  } catch (error) {
    console.error('Error during fetch:', error);
  }
}

const notifyCopyDocNo = async (text: string | null | undefined) => {
  const ok = await copyPlainText(text)
  if (ok) {
    $swal({
      icon: 'success',
      title: '已複製到剪貼簿',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 1500,
    })
  } else {
    $swal({
      icon: 'error',
      title: '複製失敗',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2000,
    })
  }
}

const tabId = ref('ExpenseRequest')
const { data: getStatusData, execute: fetchstatusDatas } = await useApi<any>(createUrl('/q/de/select/select/select/deSelect', {
  query: {
    tabId: tabId.value
  },
}))

watch(()=>filters.year,()=>{
  getProjectList()
})
watch(filters, () => {
  fetchData()
})

onMounted(() => {
  getProjectList()
  fetchData()
})

onUnmounted(() => saveListFilters())
</script>
